import { warehouseClient, extractStatusCode } from "@/infrastructure/http/warehouseClient";
import type { Receipt, ReceiptListResponse, CreateReceiptInput, AddReceiptLineInput, ApiEnvelope } from "@/domain/types";
import axios from "axios";

const ERROR_TRANSLATIONS: Record<string, string> = {
  "Stock insuficiente": "Insufficient stock",
  "Capacidad de ubicación excedida": "Location capacity exceeded",
  "No se encontró stock para el producto en la ubicación indicada": "Stock not found for this product at the specified location",
  "Verifique que el lote coincida": "Verify the lot matches",
  "Tipo de movimiento no encontrado o inactivo": "Movement type not found or inactive",
  "Only draft receipts can be edited or confirmed": "Only draft receipts can be edited or confirmed",
  "Only confirmed receipts can be cancelled": "Only confirmed receipts can be cancelled",
  "Receipt must have at least one detail line to be confirmed": "Receipt must have at least one line to be confirmed",
  "Cancellation reason is required": "Cancellation reason is required",
  "Receipt not found": "Receipt not found",
};

function getLocale(): string {
  try { return localStorage.getItem("nexo-locale") ?? "es"; } catch { return "es"; }
}

function translateError(msg: string): string {
  if (getLocale() === "es") return msg;
  for (const [es, en] of Object.entries(ERROR_TRANSLATIONS)) {
    if (msg.includes(es)) return msg.replace(es, en);
  }
  return msg;
}

function extractMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) return "Error";
  const data = err.response?.data;
  if (data && typeof data === "object" && "message" in data) {
    const msg = (data as Record<string, unknown>).message;
    if (typeof msg === "string") return translateError(msg);
    if (Array.isArray(msg) && msg.length > 0) return translateError(String(msg[0]));
  }
  return "Error";
}

export function isReceiptError(err: unknown): err is Error & { name: "ReceiptError" } {
  return err instanceof Error && err.name === "ReceiptError";
}

function throwReceiptError(err: unknown): never {
  const msg = extractMessage(err);
  const e = new Error(msg);
  e.name = "ReceiptError";
  throw e;
}

export async function listReceipts(params: { page?: number; limit?: number; status?: string; warehouseId?: string; from?: string; to?: string }): Promise<ReceiptListResponse> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.limit) query.set("limit", String(params.limit));
  if (params.status) query.set("status", params.status);
  if (params.warehouseId) query.set("warehouseId", params.warehouseId);
  if (params.from) query.set("from", params.from);
  if (params.to) query.set("to", params.to);
  const response = await warehouseClient.get<ApiEnvelope<ReceiptListResponse>>(`/receipt?${query.toString()}`);
  return response.data.data;
}

export async function getReceipt(id: string): Promise<Receipt> {
  const response = await warehouseClient.get<ApiEnvelope<Receipt>>(`/receipt/${id}`);
  return response.data.data;
}

export async function createReceipt(input: CreateReceiptInput): Promise<Receipt> {
  try {
    const response = await warehouseClient.post<ApiEnvelope<Receipt>>("/receipt", input);
    return response.data.data;
  } catch (err) { throwReceiptError(err); }
}

export async function addReceiptLine(receiptId: string, input: AddReceiptLineInput): Promise<void> {
  try {
    await warehouseClient.post(`/receipt/${receiptId}/lines`, input);
  } catch (err) { throwReceiptError(err); }
}

export async function updateReceiptLine(receiptId: string, lineId: string, input: Partial<AddReceiptLineInput>): Promise<void> {
  try {
    await warehouseClient.patch(`/receipt/${receiptId}/lines/${lineId}`, input);
  } catch (err) { throwReceiptError(err); }
}

export async function deleteReceiptLine(receiptId: string, lineId: string): Promise<void> {
  try {
    await warehouseClient.delete(`/receipt/${receiptId}/lines/${lineId}`);
  } catch (err) { throwReceiptError(err); }
}

export async function confirmReceipt(id: string): Promise<void> {
  try {
    await warehouseClient.post(`/receipt/${id}/confirm`);
  } catch (err) { throwReceiptError(err); }
}

export async function cancelReceipt(id: string, reason: string): Promise<void> {
  try {
    await warehouseClient.post(`/receipt/${id}/cancel`, { reason });
  } catch (err) { throwReceiptError(err); }
}
